/**
 * Customer Tracking Utility
 * Tracks user events for retargeting and analytics
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
  };
};

// Read a browser cookie by name
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Get Facebook click identifiers (_fbp, _fbc) for cross-domain Purchase attribution.
 * If _fbc cookie is missing but fbclid is present in the URL, synthesize it in Meta's format.
 */
export const getFbCookies = () => {
  const fbp = getCookie('_fbp');
  let fbc = getCookie('_fbc');
  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
  }
  return { fbp: fbp || null, fbc: fbc || null };
};

// Get traffic source
const getSource = () => {
  const referrer = document.referrer;
  const utmSource = new URLSearchParams(window.location.search).get('utm_source');
  
  if (utmSource) return utmSource;
  if (referrer.includes('facebook.com') || referrer.includes('fb.com')) return 'facebook';
  if (referrer.includes('instagram.com')) return 'instagram';
  if (referrer.includes('google.com')) return 'google';
  if (referrer.includes('youtube.com')) return 'youtube';
  if (!referrer) return 'direct';
  return 'referral';
};

/**
 * Track a page view
 */
export const trackPageView = async (pageUrl = window.location.href) => {
  try {
    const utmParams = getUtmParams();
    
    await fetch(`${API_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'page_view',
        page_url: pageUrl,
        source: getSource(),
        ...utmParams,
      }),
    });
  } catch (error) {
    console.error('Track page view error:', error);
  }
};

/**
 * Track add to cart event
 */
export const trackAddToCart = async (product, quantity = 1) => {
  try {
    const utmParams = getUtmParams();
    
    await fetch(`${API_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'add_to_cart',
        page_url: window.location.href,
        product_id: product.id,
        product_name: product.name,
        cart_value: product.price * quantity,
        source: getSource(),
        ...utmParams,
      }),
    });
    
    // Also trigger Facebook Pixel if available
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: product.name,
        content_ids: [product.id],
        content_type: 'product',
        value: product.price * quantity,
        currency: 'INR',
      });
    }
  } catch (error) {
    console.error('Track add to cart error:', error);
  }
};

/**
 * Track checkout started
 */
export const trackCheckoutStarted = async (cartItems, cartTotal) => {
  try {
    const utmParams = getUtmParams();
    
    await fetch(`${API_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'checkout_started',
        page_url: window.location.href,
        cart_value: cartTotal,
        source: getSource(),
        ...utmParams,
      }),
    });
    
    // Facebook Pixel
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_ids: cartItems.map(item => item.id),
        content_type: 'product',
        value: cartTotal,
        currency: 'INR',
        num_items: cartItems.length,
      });
    }
  } catch (error) {
    console.error('Track checkout error:', error);
  }
};

/**
 * Track product view
 */
export const trackProductView = async (product) => {
  try {
    const utmParams = getUtmParams();
    
    await fetch(`${API_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'product_view',
        page_url: window.location.href,
        product_id: product.id,
        product_name: product.name,
        source: getSource(),
        ...utmParams,
      }),
    });
    
    // Facebook Pixel
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: product.name,
        content_ids: [product.id],
        content_type: 'product',
        value: product.price,
        currency: 'INR',
      });
    }
  } catch (error) {
    console.error('Track product view error:', error);
  }
};

/**
 * Subscribe email
 */
export const subscribeEmail = async (email, source = 'website') => {
  try {
    const response = await fetch(`${API_URL}/api/subscribe/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source }),
    });
    
    const data = await response.json();
    
    // Facebook Pixel for lead
    if (window.fbq && data.new) {
      window.fbq('track', 'Lead', { content_name: source });
    }
    
    return data;
  } catch (error) {
    console.error('Subscribe email error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Track with email (for logged in users or after email capture)
 */
export const trackWithEmail = async (email, eventType, data = {}) => {
  try {
    const utmParams = getUtmParams();
    
    await fetch(`${API_URL}/api/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        event_type: eventType,
        page_url: window.location.href,
        source: getSource(),
        ...utmParams,
        ...data,
      }),
    });
  } catch (error) {
    console.error('Track with email error:', error);
  }
};

export default {
  trackPageView,
  trackAddToCart,
  trackCheckoutStarted,
  trackProductView,
  subscribeEmail,
  trackWithEmail,
  getFbCookies,
};
